import { Tables } from '../../db_types'
import { DolfinRpc } from '../../rpc'
import { differenceInCalendarDays } from 'date-fns'
import { parseDateValue } from '@dolfin/utils/dates'

export const getAnnualTvpProrationFactor = (
  organizationStartDate: string | null,
  yearStart: string,
  yearEnd: string
) => {
  const startDate = parseDateValue(organizationStartDate)
  const startOfYear = parseDateValue(yearStart)
  const endOfYear = parseDateValue(yearEnd)

  if (!startDate || !startOfYear || !endOfYear || startDate <= startOfYear) {
    return 1
  }

  if (startDate > endOfYear) {
    return 0
  }

  // Both the start and end dates are eligible days for TVP proration.
  const yearSpan = Math.max(differenceInCalendarDays(endOfYear, startOfYear) + 1, 1)
  return (differenceInCalendarDays(endOfYear, startDate) + 1) / yearSpan
}

export default class Tvp {
  private rpcClient: DolfinRpc

  constructor(rpcClient: DolfinRpc) {
    this.rpcClient = rpcClient
  }

  async getLatestTvp(profileId: string, referenceDate: string, organizationId?: number) {
    return this.rpcClient.getLatestTvp(profileId, referenceDate, organizationId)
  }

  async getAllocations(profileId: string, organizationId: number) {
    return this.rpcClient.getTvpAllocations(profileId, organizationId)
  }

  async getSummary(profileId: string, referenceDate: string, organizationId: number) {
    const tvpData = await this.getLatestTvp(profileId, referenceDate, organizationId)
    const tvp = Number(tvpData?.total_variable_pay || 0)

    const allocations = await this.getAllocations(profileId, organizationId)

    const totalPercentage = allocations.reduce((sum, alloc) => sum + Number(alloc.percentage), 0)
    const allocated = tvp * totalPercentage
    const remaining = tvp - allocated

    return {
      tvp,
      allocated,
      remaining,
      allocations
    }
  }

  async getTvpAllocation(
    profileId: string,
    compensationRuleVersion: Tables<'compensation_rule_versions'>,
    organizationId: number,
    referenceDate: string
  ) {
    const allocation = (
      await this.rpcClient.getTvpAllocation(profileId, organizationId, compensationRuleVersion.id)
    )?.[0]

    if (!allocation) {
      return null
    }
    const tvpData = await this.getLatestTvp(profileId, referenceDate, organizationId)

    if (!tvpData?.total_variable_pay) {
      return null
    }

    const percentage = Number(allocation.percentage)
    const allocated_amount = Number(tvpData.total_variable_pay) * percentage

    return {
      percentage,
      allocated_amount
    }
  }

  async getTvpCommissionAllocation(
    profileId: string,
    compensationRuleVersion: Tables<'compensation_rule_versions'>,
    organizationId: number,
    yearStart: string,
    yearEnd: string
  ) {
    const [allocation, organizationStartDate] = await Promise.all([
      this.getTvpAllocation(profileId, compensationRuleVersion, organizationId, yearEnd),
      this.rpcClient.getProfileOrganizationStartDate(profileId, organizationId)
    ])

    if (!allocation) {
      return null
    }

    const prorationFactor = getAnnualTvpProrationFactor(organizationStartDate, yearStart, yearEnd)

    return {
      percentage: allocation.percentage,
      allocated_amount: allocation.allocated_amount * prorationFactor,
      allocated_amount_before_proration: allocation.allocated_amount,
      proration_factor: prorationFactor
    }
  }

  async getSummaryForProfiles(profileIds: string[], referenceDate: string, organizationId: number) {
    const [tvpData, allocations] = await Promise.all([
      this.rpcClient.getTvpDataForProfiles(profileIds, referenceDate, organizationId),
      this.rpcClient.getTvpAllocationsForProfiles(profileIds, organizationId)
    ])

    // Process TVP data to get latest per profile
    const tvpByProfile: Record<string, number> = {}
    tvpData.forEach(row => {
      if (row.profile_id && tvpByProfile[row.profile_id] === undefined) {
        tvpByProfile[row.profile_id] = Number(row.total_variable_pay)
      }
    })

    // Group allocations by profile
    const allocationsByProfile: Record<
      string,
      (Tables<'tvp_allocation'> & {
        compensation_rules: { plans: { organization_id: number } } | null
      })[]
    > = {}
    allocations.forEach(alloc => {
      if (alloc.profile_id) {
        if (!allocationsByProfile[alloc.profile_id]) {
          allocationsByProfile[alloc.profile_id] = []
        }
        allocationsByProfile[alloc.profile_id].push(alloc)
      }
    })

    // Construct response
    return profileIds.map(profileId => {
      const tvp = tvpByProfile[profileId] || 0
      const userAllocations = allocationsByProfile[profileId] || []
      const totalPercentage = userAllocations.reduce(
        (sum, alloc) => sum + Number(alloc.percentage),
        0
      )
      const allocated = tvp * totalPercentage
      const remaining = tvp - allocated

      return {
        profile_id: profileId,
        tvp,
        allocated,
        remaining,
        allocations: userAllocations
      }
    })
  }

  async getLatestDraftOrVersion(
    compensationRuleId: number,
    referenceDate: string,
    isDraft: boolean
  ) {
    return this.rpcClient.getLatestCompRuleDraftOrVersion(
      compensationRuleId,
      referenceDate,
      isDraft
    )
  }

  async upsertAllocation(params: {
    profileId: string
    compensationRuleId: number
    percentage: number
    compensationRuleDraftId?: number | null
    compensationRuleVersionId?: number | null
    skipTvpBasedUpdate?: boolean
  }) {
    // Also set is_tvp_based to true on the draft/version when saving allocation
    // Skip if this is part of a batch operation
    if (!params.skipTvpBasedUpdate) {
      if (params.compensationRuleDraftId) {
        await this.rpcClient.setDraftTvpBased(params.compensationRuleDraftId, true)
      } else if (params.compensationRuleVersionId) {
        await this.rpcClient.setVersionTvpBased(params.compensationRuleVersionId, true)
      }
    }

    return this.rpcClient.upsertTvpAllocation(params)
  }

  async upsertAllocationsBatch(params: {
    allocations: Array<{
      profileId: string
      percentage: number
    }>
    compensationRuleId: number
    compensationRuleDraftId?: number | null
    compensationRuleVersionId?: number | null
  }) {
    // Set is_tvp_based once for the batch
    if (params.compensationRuleDraftId) {
      await this.rpcClient.setDraftTvpBased(params.compensationRuleDraftId, true)
    } else if (params.compensationRuleVersionId) {
      await this.rpcClient.setVersionTvpBased(params.compensationRuleVersionId, true)
    }

    // Process all allocations in parallel without updating TVP flag each time
    return Promise.all(
      params.allocations.map(allocation =>
        this.rpcClient.upsertTvpAllocation({
          profileId: allocation.profileId,
          compensationRuleId: params.compensationRuleId,
          percentage: allocation.percentage,
          compensationRuleDraftId: params.compensationRuleDraftId,
          compensationRuleVersionId: params.compensationRuleVersionId
        })
      )
    )
  }

  /**
   * Publishes the editable allocation snapshot to a compensation-rule version.
   * Replacing the snapshot is intentional: allocations removed from the draft
   * must not survive when an existing version is overridden.
   */
  async publishDraftAllocations(compensationRuleId: number, compensationRuleVersionId: number) {
    const allocations = await this.rpcClient.getDraftTvpAllocations(compensationRuleId)

    await this.rpcClient.replaceVersionTvpAllocations({
      compensationRuleId,
      compensationRuleVersionId,
      allocations
    })
  }

  async clearPublishedAllocations(compensationRuleId: number, compensationRuleVersionId: number) {
    await this.rpcClient.replaceVersionTvpAllocations({
      compensationRuleId,
      compensationRuleVersionId,
      allocations: []
    })
  }
}
